/**
 * Saldo: entitlement, used, planned and left per person for this year or next (owner,
 * 2026-09-21), with egenmelding against its limit and the next absence. The sums are saldo.ts;
 * this is the table and the three cards over it.
 */
import type { ApiAbsence, ApiAbsenceType, ApiUser } from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Empty } from '@/ui';
import { fullName, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Link } from 'react-router-dom';
import { daysOf } from './AbsenceDrawer';
import { NyttFerieaar } from './NyttFerieaar';
import { catOf } from './category';
import { periodText, spanOf } from './dates';
import { type Filter, personMatches, teamsOf } from './filters';
import { CatIcon, FilterBar, Person, YearSelect } from './parts';
import { type Balances, hasCategoryIn, saldoRows, saldoSummary } from './saldo';

/** Justert, signed: «+3», «−24», «0» */
const signed = (n: number) => (n > 0 ? `+${num(n, 1)}` : num(n, 1));
const dager = (n: number) => `${num(n, 1)} ${n === 1 ? T('dag') : T('dager')}`;

export function Saldo({
  all,
  users,
  types,
  filter,
  onFilter,
  filtersOpen,
  onFiltersOpen,
  teams,
  today,
  tenantId,
  year,
  onYear,
  onOpen,
}: {
  all: ApiAbsence[];
  users: (ApiUser & Balances)[];
  types: ApiAbsenceType[];
  filter: Filter;
  onFilter: (f: Filter) => void;
  filtersOpen: boolean;
  onFiltersOpen: (open: boolean) => void;
  teams: string[];
  today: string;
  tenantId: number | undefined;
  /** the page's holiday year (parts.tsx YearSelect); a balance is stored for this year and next only */
  year: number;
  onYear: (year: number) => void;
  onOpen: (a: ApiAbsence) => void;
}) {
  const thisYear = Number(today.slice(0, 4));
  const people = users.filter(
    (u) => personMatches(u, filter, tenantId) && (!filter.cat || hasCategoryIn(all, u.id, filter.cat, year)),
  );
  const { tenant } = useTenant();
  const rows = saldoRows(people, all, types, { year, today, basis: tenant?.vacation_day_basis });
  // Nytt ferieår (D32 B): in December the coming year as a preview, from 1 January the year itself
  const can = useCan();
  const newYear = today.slice(5, 7) === '12' ? thisYear + 1 : thisYear;
  const sum = saldoSummary(rows);

  return (
    <>
      <FilterBar
        value={filter}
        onChange={onFilter}
        teams={teams}
        placeholder={T('Søk ansatt eller team')}
        open={filtersOpen}
        onOpen={onFiltersOpen}
      >
        <YearSelect value={year} onChange={onYear} today={today} />
        {can('ansatte.endre') && <NyttFerieaar year={newYear} />}
      </FilterBar>
      <div className="kpi fkpi" data-testid="saldo-cards">
        <div>
          <span className="kic tn-ok">
            <Icon name="sun" />
          </span>
          <span>
            {T('Gjennomsnitt gjenstående')}
            <b>{sum.avgLeft == null ? '–' : dager(Math.round(sum.avgLeft * 10) / 10)}</b>
            <i>{sum.avgEntitled == null ? '' : `${T('av')} ${dager(Math.round(sum.avgEntitled))}`}</i>
          </span>
        </div>
        <div>
          <span className="kic tn-blue">
            <Icon name="calendar" />
          </span>
          <span>
            {T('Ansatte med planlagt ferie')}
            <b>
              {sum.withPlanned} {T('av')} {sum.total}
            </b>
            <i>{T('har innvilget ferie fremover')}</i>
          </span>
        </div>
        <div className={sum.low ? 'w' : ''}>
          <span className="kic tn-warn">
            <Icon name="warn" />
          </span>
          <span>
            {T('Lav saldo')}
            <b>
              {sum.low} {sum.low === 1 ? T('ansatt') : T('ansatte')}
            </b>
            <i>{T('har 5 dager eller mindre igjen')}</i>
          </span>
        </div>
      </div>
      <section className="mcard">
        <div className="mch">
          <h2>{T('Feriesaldo {year}', { year })}</h2>
        </div>
        {rows.length ? (
          <table className="mt cards fs">
            <thead>
              <tr>
                <th>{T('Ansatt')}</th>
                <th className="num">{T('Rettighet')}</th>
                <th
                  className="num"
                  title={T(
                    'Endret utenom innvilget ferie: satt for hånd, overført, startsaldo eller over saldo',
                  )}
                >
                  {T('Justert')}
                </th>
                <th className="num">{T('Brukt')}</th>
                <th className="num">{T('Planlagt')}</th>
                <th>{T('Gjenstår')}</th>
                <th>{T('Egenmelding')}</th>
                <th>{T('Neste fravær')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const nx = r.next ? spanOf(r.next) : null;
                // the bar draws the days used, neutral, so a full year left is an empty bar (#272 B-40): the approved
                // holiday, not Rettighet − Gjenstår, which would draw Justert as days used (a late starter stored at 0)
                const pct =
                  r.left != null && r.entitled
                    ? Math.max(0, Math.min(1, (r.used + r.planned) / r.entitled))
                    : 0;
                const full = r.egen.maxTimes != null && r.egen.times >= r.egen.maxTimes;
                return (
                  <tr key={r.u.id} data-testid="saldo-row">
                    <td>
                      <Link to={`/ansatte/${r.u.id}`} className="plain">
                        <Person
                          id={r.u.id}
                          name={fullName(r.u)}
                          sub={
                            teamsOf(r.u, tenantId)
                              .map((t) => t.name)
                              .join(', ') || undefined
                          }
                        />
                      </Link>
                    </td>
                    <td className="num" data-k={T('Rettighet')}>
                      {r.entitled ?? '–'}
                      {/* ferieloven § 5 nr. 3 (ADR 0024): a start after 30 September of the year */}
                      {r.late != null && (
                        <small data-testid="saldo-late-start">
                          {year === thisYear
                            ? T('Én uke i år: begynte etter 30. september')
                            : T('Én uke i {year}: begynner etter 30. september', { year })}
                        </small>
                      )}
                    </td>
                    {/* what moved the balance besides approved holiday, so the row adds up (regression review 2026-11-04) */}
                    <td className="num" data-k={T('Justert')} data-testid="saldo-adjusted">
                      {r.adjusted == null ? '–' : signed(r.adjusted)}
                    </td>
                    <td className="num" data-k={T('Brukt')}>
                      {num(r.used, 1)}
                    </td>
                    <td className="num" data-k={T('Planlagt')}>
                      {num(r.planned, 1)}
                    </td>
                    <td className="nw fs-left">
                      {r.left == null ? (
                        '–'
                      ) : (
                        <span className="fbal">
                          <span className={`fbar2 used${r.left <= 5 ? ' low' : ''}`}>
                            <i style={{ width: `${pct * 100}%` }} />
                          </span>
                          {/* one figure format, «x av y» (#272 B-22, V-08) */}
                          <span className={r.left <= 5 ? 'fwarn' : undefined}>
                            <b>{num(r.left, 1)}</b> {T('av')} {r.entitled ?? '–'} {T('dager')}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className={`nw fs-egen${full ? ' fwarn' : ''}`} data-k={T('Egenmelding')}>
                      {r.egen.maxTimes != null
                        ? T('{n} av {max} ganger', { n: r.egen.times, max: r.egen.maxTimes })
                        : r.egen.times === 1
                          ? T('1 gang')
                          : T('{n} ganger', { n: r.egen.times })}
                      <small>
                        {r.egen.maxDays != null
                          ? T('{n} av {max} dager, siste 12 mnd.', { n: r.egen.days, max: r.egen.maxDays })
                          : T('{n} dager, siste 12 mnd.', { n: r.egen.days })}
                      </small>
                    </td>
                    {/* no next absence: «–» in the table's column; a card drops the line (#272 B-T03) */}
                    <td className={r.next && nx ? 'fs-next' : 'fs-next none'}>
                      {r.next && nx ? (
                        <button type="button" className="lnk fnext" onClick={() => r.next && onOpen(r.next)}>
                          <CatIcon cat={catOf(r.next.absence_type)} />
                          <span>
                            {periodText(nx.from, nx.to)}
                            <small>
                              {r.next.absence_type?.name ?? T('Fravær')}, {dager(daysOf(nx.from, nx.to))}
                              {r.next.status === 'pending' ? `, ${T('søknad')}` : ''}
                            </small>
                          </span>
                        </button>
                      ) : (
                        '–'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Empty>{T('Ingen ansatte passer filteret.')}</Empty>
        )}
      </section>
      <p className="mfoot" style={{ maxWidth: '72ch' }}>
        {T(
          'Gjenstår er feriesaldoen slik den står på personen. Brukt og planlagt er innvilget ferie, med dagene som ble trukket da den ble innvilget; planlagt er ikke startet ennå. Justert er det saldoen er endret med utenom innvilget ferie, for eksempel satt for hånd, overført fra i fjor eller startsaldo, så Rettighet + Justert − Brukt − Planlagt = Gjenstår.',
        )}
      </p>
    </>
  );
}
